import { ChangeDetectorRef, Component, ComponentFactoryResolver, ElementRef, OnDestroy, ViewChild, ViewContainerRef } from "@angular/core";
import { MatDialogRef } from "@angular/material/dialog";
import type { ModalComponentInterface, ModalContainerInterface, ModalResultInterface, ModalViewModelInterface, ResultCommandInterface, UICommandInterface, UIResultCommandInterface } from '@nts/std/interfaces';
import { BehaviorSubject, Observable, take } from "rxjs";
import { ModalResult } from "../../view-models/modal/modal-result";
import { ComponentLocator } from "../component-locator";
import { ModalCommandWrapper, UICommandSettingsManager, UIResultCommand } from "../../view-models/commands";
import { ViewModelTypeInspector } from "../../decorators/view-model-type.decorator";
import { MessageResourceManager } from "../../resources/message-resource-manager";
import { MetaDataDescriptions } from "../../meta-data/meta-data-descriptions";
import { MessageResult } from "@nts/std/types";
import { UntilDestroy } from "@ngneat/until-destroy";

@UntilDestroy()
@Component({ template: '' })
export abstract class BaseModalContainerComponent implements ModalContainerInterface, OnDestroy {

    @ViewChild('documentPlaceHolder', { read: ViewContainerRef, static: true })
    documentPlaceHolder: ViewContainerRef;

    title: string;
    cancelCommand: UICommandInterface;
    active: boolean;
    maximized = false;
    onClose = new BehaviorSubject<ModalResultInterface<any>>(new ModalResult(null, true));
    maximizable = false;
    commands = new Array<ModalCommandWrapper<any>>();
    dragPosition = { x: 0, y: 0 };

    constructor(
        protected el: ElementRef,
        protected componentFactoryResolver: ComponentFactoryResolver,
        // private _bsModalRef: BsModalRef,
        protected componentLocator: ComponentLocator,
        protected cd: ChangeDetectorRef,
        protected dialogRef: MatDialogRef<any>,
    ) { }

    ngOnDestroy(): void {
        if (this.viewModel?.onModalClosed) {
            this.viewModel.onModalClosed.next();
        }
        if (this.viewModel?.destroySubscribers$) {
            this.viewModel.destroySubscribers$.next();
        }
    }

    resetPosition() {
        this.dragPosition = { x: 0, y: 0 };
    }

    toggleMaximize() {
        this.maximized = !this.maximized;
        this.setMaximizedClass();
        this.resetPosition();
    }

    viewModel: ModalViewModelInterface<any, any>;

    showCustomModalWithResultAsync<TResult>(
        viewModel: ModalViewModelInterface<any, TResult>,
        showCancel = true,
        maximizable = false,
        maximized = false
    ): Promise<ModalResultInterface<TResult>> {
        return this.createComponent(viewModel, showCancel, maximizable, maximized);
    }

    protected async createComponent(
        viewModel: ModalViewModelInterface<any, any>,
        showCancel = true,
        maximizable = false,
        maximized = false) {

        this.viewModel = viewModel;
        const viewModelType = ViewModelTypeInspector.getValue(this.viewModel);
        const componentType = this.componentLocator.getComponentType(viewModelType);
        const componentFactory = this.componentFactoryResolver.resolveComponentFactory(componentType);
        const componentRef = this.documentPlaceHolder.createComponent(componentFactory);
        const componentInstance = componentRef.instance as ModalComponentInterface;
        componentInstance.viewModel = viewModel;
        if (componentInstance.afterOpened) {
            this.dialogRef.afterOpened().pipe(this.untilDestroyed(this), take(1)).subscribe(() => componentInstance.afterOpened());
        }

        // Intercetta il backdropClick
        this.dialogRef.backdropClick().pipe(this.untilDestroyed(this)).subscribe(() => this.handleModalClose());

        // Intercetta il tasto ESC
        this.dialogRef.keydownEvents().pipe(this.untilDestroyed(this)).subscribe((event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                this.handleModalClose();
            }
        });

        this.active = true;
        this.maximizable = maximizable;
        this.maximized = maximized;

        this.setMaximizedClass();

        if (showCancel) {
            viewModel.modalCommands.push(this.createButton(MessageResult.Cancel));
        }

        viewModel.modalCommands.forEach(command => {
            const commandWrapper = new ModalCommandWrapper(command, commandResult => {
                if (command.closeModal) {
                    this.closeCore(command, commandResult);
                }
                return commandResult;
            });
            if (command instanceof UIResultCommand) {
                this.commands.push(commandWrapper);
            }
        });

        this.commands.sort((a, b) => {
            return (a.internalCommand.isDefault === b.internalCommand.isDefault) ? 0 : a.internalCommand.isDefault ? 1 : -1;
        });

        if (viewModel.cancelAction) {
            viewModel.cancelAction.pipe(this.untilDestroyed(this)).subscribe(() => {
                this.closeModal();
            });
        }

        if (componentInstance.initialize) {
            await componentInstance.initialize();
        }

        if (this.viewModel?.changed) {
            this.viewModel.changed.pipe(this.untilDestroyed(this)).subscribe(() => {
                this.cd.detectChanges();
            });
        }
        this.cd.detectChanges();
        return new Promise<ModalResultInterface<any>>((resolve, reject) => {
            this.dialogRef.afterClosed().pipe(
                take(1)
            ).subscribe(_ => {
                resolve(this.onClose.value);
            });
        });
    }

    private closeCore<TModalResult>(source: ResultCommandInterface<TModalResult>, result: TModalResult) {
        const modalResult = new ModalResult(result, source.isCancel);
        this.onClose.next(modalResult);
        this.dialogRef.close();
    }

    private setMaximizedClass() {
        if (!this.maximized) {
            this.dialogRef.removePanelClass('fullscreen-dialog');
        } else {
            this.dialogRef.addPanelClass('fullscreen-dialog');
        }
    }

    abstract untilDestroyed<T>(instance: T, destroyMethodName?: keyof T): <U>(source: Observable<U>) => Observable<U>;

    protected createButton(
        result: MessageResult,
        isDefault = false,
        metaDataDescriptions?: MetaDataDescriptions,
        execute = async (x) => result
    ): UIResultCommandInterface<MessageResult> {

        const cmd = new UIResultCommand(null, execute);

        cmd.displayName = result.toString();

        if (metaDataDescriptions?.displayNameKey) {
            cmd.displayName = MessageResourceManager.Current.getMessage(metaDataDescriptions.displayNameKey);
        }

        if (metaDataDescriptions?.displayName) {
            cmd.displayName = metaDataDescriptions?.displayName;
        }

        cmd.description = cmd.displayName;

        if (metaDataDescriptions?.descriptionKey) {
            cmd.description = MessageResourceManager.Current.getMessage(metaDataDescriptions.descriptionKey);
        }

        if (metaDataDescriptions?.description) {
            cmd.description = metaDataDescriptions?.description;
        }

        cmd.tooltip = cmd.description;
        cmd.name = result.toString();
        cmd.isCancel = result === MessageResult.Cancel;
        cmd.isDefault = isDefault;

        if (!metaDataDescriptions) {
            const manager = new UICommandSettingsManager();
            const stringifiedEnum = MessageResult[result];
            manager.setUICommandByString(stringifiedEnum, cmd);
        }

        return cmd;
    }

    async closeModal() {
        await this.handleModalClose();
    }

    private async handleModalClose() {
        let confirmClose = true;
        if (this.viewModel.closeModal != null) {
            confirmClose = await this.viewModel.closeModal();
        }
        if (confirmClose) {
            this.closeCore(this.createButton(MessageResult.Cancel), null);
        }
    }

}

